import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listDirectoryCompanies,
  getCompanyFacets,
  createCompany,
  getDirectoryCompany,
} from "@/api/companies";
import type { CompanyInput } from "@sales-ops-amplify/shared";

const COMPANIES_QUERY_KEY = ["companies"] as const;

export function useDirectoryCompanies(params?: {
  search?: string;
  industry?: string;
  country?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "directory", params ?? {}],
    queryFn: () => listDirectoryCompanies(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useDirectoryCompany(companyId: string | undefined) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "detail", companyId],
    queryFn: () => getDirectoryCompany(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

export function useCompanyFacets() {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "facets"],
    queryFn: getCompanyFacets,
    staleTime: 120_000,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) => createCompany(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    },
  });
}

// Re-export workspace hooks so existing `from "@/hooks/useCompanies"` imports keep working.
export {
  useAccounts,
  useAccount,
  useUpdateAccount,
  useResearchAccount,
  useAccountActivities,
  useCompanyActivities,
  useLogAccountActivity,
  useLogCompanyActivity,
} from "./useAccounts";
export {
  useLeads,
  useLead,
  useCreateLead,
  useUpdateLead,
} from "./useLeads";
